import { createFileRoute, notFound } from "@tanstack/react-router"

import { authorDescription, authorSchema, authorUrl, getBlogAuthors } from "@/lib/blog-authors"
import { resolveContent } from "@/lib/content-resolver"
import { RSS_FEED_LINK } from "@/lib/rss-meta"
import { seoMeta } from "@/lib/seo"

import { BlogAuthorPage } from "../-components/BlogAuthorPage"

const modules = import.meta.glob("../-content/*.{md,mdx}", { eager: true }) as Record<
  string,
  { default: React.ComponentType; frontmatter: Record<string, unknown> }
>
const blogPosts = resolveContent(modules)

const blogAuthors = [
  ...new Map(
    blogPosts
      .flatMap((post) => getBlogAuthors(post.frontmatter["author"]))
      .map((author) => [author.slug, author]),
  ).values(),
]

function getAuthorPosts(slug: string) {
  return blogPosts.filter((post) =>
    getBlogAuthors(post.frontmatter["author"]).some((author) => author.slug === slug),
  )
}

export const Route = createFileRoute("/_content/blog/authors/$slug")({
  loader: ({ params }) => {
    const author = blogAuthors.find((author) => author.slug === params.slug)
    if (!author) throw notFound()
    return { author }
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {}
    const author = loaderData.author
    const url = authorUrl(author)
    return {
      ...seoMeta({
        title: `${author.name} · Articles`,
        description: authorDescription(author),
        url,
        type: "profile",
        image: `https://entire.com/og/author/${encodeURIComponent(author.slug)}.png`,
        imageAlt: `${author.name} · ${author.title}`,
        extraLinks: [RSS_FEED_LINK],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            url,
            name: author.name,
            mainEntity: authorSchema(author),
          }),
        },
      ],
    }
  },
  component: BlogAuthorRoute,
})

function BlogAuthorRoute() {
  const { author } = Route.useLoaderData()
  return <BlogAuthorPage author={author} posts={getAuthorPosts(author.slug)} />
}
